/* Settings + Clinical Export + Recovery Mode + WhatsApp ICE test + PWA install hint. */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircleGear, PaperPlaneTilt, WhatsappLogo, MoonStars, Sun, DownloadSimple } from "@phosphor-icons/react";
import health from "./healthApi";

export default function Settings({ recovery, setRecovery }) {
  const [s, setS] = useState(null);
  const [sending, setSending] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect if already installed / running as PWA
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches
                       || window.navigator?.standalone === true;
    setIsStandalone(!!standalone);
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!installEvent) {
      toast.info("On iPhone: tap the Share icon in Safari, then 'Add to Home Screen'");
      return;
    }
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") toast.success("NeuroLog installed to your home screen");
    setInstallEvent(null);
  };

  useEffect(() => {
    health.get("/settings").then((r) => {
      const data = r.data;
      // Ensure emergency_contacts is always exactly 3 slots for the UI
      const cs = Array.isArray(data.emergency_contacts) ? data.emergency_contacts : [];
      while (cs.length < 3) cs.push({ name: "", phone: "" });
      setS({ ...data, emergency_contacts: cs.slice(0, 3) });
    });
  }, []);

  if (!s) return <div className="text-center text-xs py-8" style={{ color: "var(--nl-muted)" }}>Loading…</div>;

  const updateContact = (idx, key, value) => {
    const cs = [...s.emergency_contacts];
    cs[idx] = { ...cs[idx], [key]: value };
    setS({ ...s, emergency_contacts: cs });
  };

  const save = async () => {
    try {
      const payload = { ...s };
      // Strip empty rows before sending — backend will keep the first as legacy primary
      payload.emergency_contacts = (payload.emergency_contacts || []).filter((c) => (c.phone || "").trim());
      if (!payload.doctor_email) payload.doctor_email = null;
      await health.put("/settings", payload);
      toast.success(`Saved · ${payload.emergency_contacts.length} contact(s) configured`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : (detail || "Save failed");
      toast.error(msg);
    }
  };

  const sendExport = async () => {
    if (!s.doctor_email) return toast.error("Add a doctor email first");
    setSending(true);
    try {
      const { data } = await health.post("/export/email", { to_email: s.doctor_email, days: 90 });
      toast.success(`90-day report emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="nl-settings-tab" className="space-y-4">
      <div>
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-accent)" }}>SETTINGS</div>
        <div className="font-display text-2xl font-black" style={{ color: "var(--nl-text)" }}>Emergency & clinical</div>
      </div>

      {!isStandalone && (
        <div
          data-testid="nl-install-card"
          className="rounded-lg p-4"
          style={{ background: "linear-gradient(135deg,rgba(72,202,228,0.14),rgba(11,19,43,0.4))",
                   border: "1px solid var(--nl-accent)" }}
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "var(--nl-accent-dim)", border: "1px solid var(--nl-accent)" }}>
              <DownloadSimple size={18} weight="bold" color="#48CAE4" />
            </div>
            <div className="flex-1">
              <div className="font-display font-black text-sm" style={{ color: "var(--nl-text)" }}>
                Install as an app
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--nl-muted)" }}>
                Save to your home screen — one-tap access, no URL typing, full-screen.
              </div>
              <button
                data-testid="nl-install-btn"
                onClick={install}
                className="mt-3 h-10 px-4 rounded-lg font-label text-xs tracking-widest inline-flex items-center gap-2"
                style={{ background: "var(--nl-accent)", color: "#001018" }}
              >
                <DownloadSimple size={14} weight="bold" />
                {installEvent ? "INSTALL NOW" : "SHOW ME HOW"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg p-4 space-y-3" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-muted)" }}>EMERGENCY CONTACTS · UP TO 3 · ALL ALERTED AT ONCE</div>
        {s.emergency_contacts.map((c, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 pt-2 first:pt-0" style={{ borderTop: i > 0 ? "1px dashed var(--nl-line)" : "none" }}>
            <Field label={`#${i + 1} · Name`}>
              <input value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)} className="nl-input" placeholder={i === 0 ? "e.g. Partner" : "Optional"} data-testid={`nl-contact-name-${i}`} />
            </Field>
            <Field label="WhatsApp (E.164)">
              <input value={c.phone} onChange={(e) => updateContact(i, "phone", e.target.value)} className="nl-input" placeholder="+2782xxxxxxx" data-testid={`nl-contact-phone-${i}`} />
            </Field>
          </div>
        ))}
        <Field label="Distress message (auto-sent)">
          <textarea rows={2} value={s.default_distress_message} onChange={(e) => setS({ ...s, default_distress_message: e.target.value })} className="nl-input" style={{ resize: "none" }} />
        </Field>
      </div>

      <div className="rounded-lg p-4 space-y-3" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-muted)" }}>DOCTOR (CLINICAL EXPORT)</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Doctor name"><input value={s.doctor_name} onChange={(e) => setS({ ...s, doctor_name: e.target.value })} className="nl-input" /></Field>
          <Field label="Doctor email"><input type="email" value={s.doctor_email || ""} onChange={(e) => setS({ ...s, doctor_email: e.target.value })} className="nl-input" data-testid="nl-settings-doctor-email" placeholder="doctor@…" /></Field>
        </div>
        <button
          data-testid="nl-email-export"
          onClick={sendExport}
          disabled={sending || !s.doctor_email}
          className="w-full h-11 rounded-lg font-label text-xs tracking-widest flex items-center justify-center gap-2 disabled:opacity-40"
          style={{ background: "var(--nl-accent)", color: "#001018" }}
        >
          <PaperPlaneTilt size={14} weight="fill" /> {sending ? "SENDING…" : "EMAIL 90-DAY REPORT TO DOCTOR"}
        </button>
      </div>

      <div className="rounded-lg p-4 space-y-2" style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-muted)" }}>DISPLAY</div>
        <button
          data-testid="nl-recovery-toggle"
          onClick={() => setRecovery(!recovery)}
          className="w-full h-11 rounded-lg font-label text-xs tracking-widest flex items-center justify-center gap-2"
          style={{ background: recovery ? "var(--nl-accent)" : "transparent",
                   color: recovery ? "#001018" : "var(--nl-accent)",
                   border: `1px solid var(--nl-accent)` }}
        >
          {recovery ? <Sun size={14} /> : <MoonStars size={14} />}
          {recovery ? "DISABLE RECOVERY MODE" : "ENABLE RECOVERY MODE (dim screen)"}
        </button>
      </div>

      <button data-testid="nl-settings-save" onClick={save} className="w-full h-11 rounded-lg font-label text-xs tracking-widest" style={{ background: "var(--nl-accent)", color: "#001018" }}>
        SAVE SETTINGS
      </button>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="mb-1 font-label text-[10px] tracking-widest" style={{ color: "var(--nl-muted)" }}>{label}</div>
    {children}
  </label>
);
